import { lessonAtom } from "@core/grades/atoms";
import { useIsLoggedIn } from "@framework/account/hooks";
import { trans } from "@mongez/localization";
import {
  ArrowDownToLine,
  BadgeCheck,
  CheckCircle2,
  Clock3,
  LockKeyhole,
  Sparkles,
} from "lucide-react";
import { useLessonQuickActions } from "../../hooks";
import { usePurchaseLesson } from "../../hooks/lesson-details.hooks";

export function LessonQuickActions() {
  const isPaid = lessonAtom.use("isPaid");
  const isFree = lessonAtom.use("isFree");
  const paymentStatus = lessonAtom.use("paymentStatus");
  const isLoggedIn = useIsLoggedIn();

  const { buttonText, priceText, priceSubtext, handleStartLearning } =
    useLessonQuickActions();

  const { purchaseLesson } = usePurchaseLesson();

  return (
    <aside className="sticky top-24 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/40 dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/20">
      <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-violet-50 p-5 dark:border-slate-800 dark:from-slate-900 dark:to-violet-950/30">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-white dark:bg-white dark:text-slate-950">
            <Sparkles className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-black text-slate-950 dark:text-white">
              {trans("grades.quickActions")}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {trans("grades.accessLessonContent")}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {!isPaid && (
          <div className="rounded-2xl bg-slate-50 p-4 text-center dark:bg-slate-900">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
              {trans("grades.lessonPrice")}
            </p>
            <div className="mt-1 text-3xl font-black text-slate-950 dark:text-white">
              {priceText}
            </div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {priceSubtext}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {isPaid ? (
            <>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500 text-white">
                    <BadgeCheck className="h-6 w-6" />
                  </span>
                  <div>
                    <h4 className="font-black text-emerald-900 dark:text-emerald-100">
                      {trans("grades.lessonUnlocked")}
                    </h4>
                    <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                      {trans("grades.readyToLearn")}
                    </p>
                  </div>
                </div>
              </div>
              <button
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-3.5 font-black text-white shadow-lg shadow-violet-200 transition hover:-translate-y-0.5 hover:shadow-xl dark:shadow-none"
                onClick={handleStartLearning}
              >
                <ArrowDownToLine className="h-5 w-5" />
                {trans("startLearning")}
              </button>
            </>
          ) : paymentStatus === "pending" ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
              <div className="flex items-center gap-2 font-black text-amber-900 dark:text-amber-100">
                <Clock3 className="h-5 w-5" />
                {trans("grades.paymentUnderReview")}
              </div>
              <p className="mt-2 text-xs leading-5 text-amber-700 dark:text-amber-300">
                {trans("grades.paymentUnderReviewMessage")}
              </p>
            </div>
          ) : isFree && isLoggedIn ? (
            <>
              <div className="flex items-center justify-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-700 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300">
                <CheckCircle2 className="h-5 w-5" />
                {trans("grades.freeLessonAvailable")}
              </div>
              <button
                className="w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3.5 font-black text-white shadow-lg shadow-emerald-200 transition hover:-translate-y-0.5 dark:shadow-none"
                onClick={purchaseLesson}
              >
                {trans("grades.unlockForFree")}
              </button>
            </>
          ) : (
            <button
              onClick={purchaseLesson}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3.5 font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-violet-700 dark:bg-white dark:text-slate-950 dark:hover:bg-violet-200"
            >
              <LockKeyhole className="h-5 w-5" />
              {buttonText}
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
